// What an Industry record (Admin → Industries) means for the public pages — the ONLY place that interprets its fields.
// Pure functions (no API, no aliases) so the page, the cards, the mega menu, the product page and the SEO prerender all agree.
//
//   name / slug / icon / short_description / hero_image / image / status / order / featured    basic info
//   hero { eyebrow, heading, highlight, description }        overview { eyebrow, heading, description }
//   productLines { heading, items: [{ category_slug, subcategory_slug?, label? }] }     references to categories / subcategories
//   industryProducts { heading, productIds: [] }                                        references to Products (ordered)
//   relatedIndustries [industry ids]                                                    references to other Industries (ordered)
//   cta { heading, text, buttonLabel, buttonLink }
//
// A blank text field means "use the built-in wording" (the fallbacks below), never "hide the section title".

export const DEFAULTS = {
  heroEyebrow: "Industry Solutions",
  overviewEyebrow: "Industry Overview",
  linesEyebrow: "Our Solutions",
  productsEyebrow: "Related Products",
  ctaButtonLabel: "Request a Quote",
  ctaButtonLink: "/contact",
  ctaText: "Share your industry, application details and quantity with our team. We can help identify a suitable equipment option based on your working requirements.",
};

const text = (v) => (typeof v === "string" ? v.trim() : "");
const titleCase = (slug) => String(slug || "").split("-").filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");

export const heroContent = (industry) => {
  const h = industry?.hero || {};
  return {
    eyebrow: text(h.eyebrow) || DEFAULTS.heroEyebrow,
    heading: text(h.heading) || industry?.name || "",
    highlight: text(h.highlight),
    // `description` is the older single description field: still shown until the admin saves the hero text explicitly.
    description: h.description === undefined ? text(industry?.description) : text(h.description),
  };
};

export const overviewContent = (industry) => {
  const o = industry?.overview || {};
  return {
    eyebrow: text(o.eyebrow) || DEFAULTS.overviewEyebrow,
    heading: text(o.heading) || `Equipment for ${industry?.name || ""}`,
    description: o.description === undefined ? text(industry?.description) : text(o.description),
  };
};

export const linesHeading = (industry) => text(industry?.productLines?.heading) || `Product Lines for ${industry?.name || ""}`;
export const productsHeading = (industry) => text(industry?.industryProducts?.heading) || `Products for ${industry?.name || ""}`;

export const ctaContent = (industry) => {
  const c = industry?.cta || {};
  return {
    heading: text(c.heading) || (industry?.name ? `Looking for Equipment for ${industry.name}?` : "Looking for the Right Solution for Your Industry?"),
    text: text(c.text) || DEFAULTS.ctaText,
    buttonLabel: text(c.buttonLabel) || DEFAULTS.ctaButtonLabel,
    buttonLink: text(c.buttonLink) || DEFAULTS.ctaButtonLink,
  };
};

/** The saved product-line references, as stored. */
export const industryLineRefs = (industry) => (Array.isArray(industry?.productLines?.items) ? industry.productLines.items : []).filter((x) => x && x.category_slug);

/**
 * Link + label list for the industry's product lines. Titles are read from the live category tree (`categories`, each with
 * `subcategories`) so renaming a category or subcategory in Admin → Mega Menu shows up here; `label` is an optional override.
 */
export function industryLines(industry, categories = []) {
  const bySlug = new Map(categories.map((c) => [c.slug, c]));
  return industryLineRefs(industry).map((ref) => {
    const cat = bySlug.get(ref.category_slug);
    const sub = ref.subcategory_slug ? (cat?.subcategories || []).find((s) => s.slug === ref.subcategory_slug) : null;
    const name = text(ref.label) || (ref.subcategory_slug ? sub?.title : cat?.title) || titleCase(ref.subcategory_slug || ref.category_slug);
    return {
      key: ref.subcategory_slug ? `s:${ref.category_slug}/${ref.subcategory_slug}` : `c:${ref.category_slug}`,
      name,
      to: ref.subcategory_slug ? `/products/${ref.category_slug}/${ref.subcategory_slug}` : `/products/${ref.category_slug}`,
      category_slug: ref.category_slug,
      subcategory_slug: ref.subcategory_slug || "",
    };
  });
}

/** Distinct category slugs the industry's product lines belong to (in order). */
export const industryCategorySlugs = (industry) => [...new Set(industryLineRefs(industry).map((r) => r.category_slug))];

/** Same, as `{ slug, title }` using the live category titles. */
export const industryCategories = (industry, categories = []) => {
  const bySlug = new Map(categories.map((c) => [c.slug, c]));
  return industryCategorySlugs(industry).map((slug) => ({ slug, title: bySlug.get(slug)?.title || titleCase(slug) }));
};

export const industryProductIds = (industry) => (Array.isArray(industry?.industryProducts?.productIds) ? industry.industryProducts.productIds : []).filter(Boolean);

/** The industries the admin selected as "similar", in the selected order (published ones only — `all` is the public list). */
export function relatedIndustries(industry, all = []) {
  const byId = new Map(all.map((i) => [i.id, i]));
  return (Array.isArray(industry?.relatedIndustries) ? industry.relatedIndustries : [])
    .map((id) => byId.get(id))
    .filter((i) => i && i.id !== industry.id);
}

/** Resolve saved product ids into products, in saved order, skipping ones that no longer exist (or are unpublished). */
export const orderProducts = (ids, products = []) => {
  const byId = new Map(products.map((p) => [p.id, p]));
  return ids.map((id) => byId.get(id)).filter((p) => p && p.name && p.status !== "draft");
};

/** Names of the industry's product lines (SEO description); `categories` is optional — without it the slugs are prettified. */
export const industryLineNames = (industry, categories = []) => industryLines(industry, categories).map((l) => l.name);
